import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FaBars, FaTimes, FaSearch, FaUser } from 'react-icons/fa';

// Import our custom Logo component - we'll keep this as an option
import Logo from './Logo';
// Import Auth Context
import { useAuth } from '../firebase/AuthContext';
// Import CartIcon
import CartIcon from './cart/CartIcon';
// Import MetalPricesTicker and CategoryNav
import MetalPricesTicker from './MetalPricesTicker';
import CategoryNav from './CategoryNav';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const { currentUser, logout } = useAuth();
  
  // You can set this to true to use the SVG component, or false to use an image file
  const useSvgComponent = false;
  // Path to your logo image (PNG, JPG, WebP, etc.)
  const logoImagePath = '/images/logo.png';

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
    setShowUserMenu(false);
  }, [location]);

  // Handle user logout
  const handleLogout = async () => {
    try {
      await logout();
      // Redirect will happen automatically via PrivateRoute
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/categories?search=${encodeURIComponent(searchQuery)}`;
      setShowSearch(false);
      setSearchQuery('');
    }
  };

  return (
    <HeaderWrapper>
      <MetalPricesTicker />
      <HeaderContainer $scrolled={scrolled}>
        <HeaderContent>
          <LogoContainer>
            <Link to="/">
              <LogoWrapper>
                {useSvgComponent ? (
                  <Logo width={200} height={60} />
                ) : (
                  <LogoImage 
                    src={logoImagePath} 
                    alt="Master Collectors Forge Logo" 
                  />
                )}
              </LogoWrapper>
            </Link>
          </LogoContainer>

          <SearchBarContainer $show={showSearch}>
            <SearchForm onSubmit={handleSearch}>
              <SearchInput
                type="text"
                placeholder="Search for coins, gems, collectibles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <SearchSubmitButton type="submit">
                <FaSearch />
              </SearchSubmitButton>
            </SearchForm>
          </SearchBarContainer>

          <NavWrapper>
            <CategoryNav />
          </NavWrapper>

          <HeaderActions>
            <MobileMenuButton onClick={() => setIsMenuOpen(!isMenuOpen)}>
              {isMenuOpen ? <FaTimes /> : <FaBars />}
              <MenuText>Menu</MenuText>
            </MobileMenuButton>
            
            <SearchButton onClick={() => setShowSearch(!showSearch)}>
              <FaSearch />
            </SearchButton>
            <CartIcon />
            
            {currentUser ? (
              <UserMenuContainer>
                <UserButton onClick={() => setShowUserMenu(!showUserMenu)}>
                  {currentUser.photoURL ? (
                    <UserAvatar src={currentUser.photoURL} alt="Profile" />
                  ) : (
                    <FaUser />
                  )}
                </UserButton>
                {showUserMenu && (
                  <UserDropdown>
                    <UserInfo>
                      <strong>{currentUser.displayName || 'User'}</strong>
                      <small>{currentUser.email}</small>
                    </UserInfo>
                    <DropdownDivider />
                    <DropdownItem to="/dashboard">Dashboard</DropdownItem>
                    <DropdownItem to="/profile">Profile</DropdownItem>
                    <DropdownDivider />
                    <DropdownButton onClick={handleLogout}>Log Out</DropdownButton>
                  </UserDropdown>
                )}
              </UserMenuContainer>
            ) : (
              <AuthButtons>
                <LoginButton to="/login">Log In</LoginButton>
                <SignupButton to="/signup">Sign Up</SignupButton>
              </AuthButtons>
            )}
          </HeaderActions>
        </HeaderContent>
        
        <MobileNavDrawer $isOpen={isMenuOpen}>
          <MobileNavContent>
            <CategoryNav isMobile={true} />
          </MobileNavContent>
        </MobileNavDrawer>
      </HeaderContainer>
      
      {isMenuOpen && <Overlay onClick={() => setIsMenuOpen(false)} />}
    </HeaderWrapper>
  );
};

// Styled Components
const HeaderWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1001;
  background: ${({ theme }) => `linear-gradient(to right, ${theme.colors.primary}, ${theme.colors.secondary})`};
`;

const HeaderContainer = styled.header`
  position: relative;
  width: 100%;
  background: transparent;
  transition: all ${({ theme }) => theme.transitions.medium};
  padding: 0.75rem 0;
  border-bottom: 2px solid ${({ theme }) => theme.colors.metallic.gold};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding: 0.35rem 0;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 1.5rem;
  gap: 2rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    gap: 0.5rem;
    padding: 0 0.5rem;
  }
`;

const NavWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: none;
  }
`;

const LogoContainer = styled.div`
  z-index: 1;
`;

const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    transform: scale(0.7);
    transform-origin: left center;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    transform: scale(0.6);
  }
`;

const LogoImage = styled.img`
  height: 60px;
  width: auto;
  max-width: 200px;
  object-fit: contain;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    height: 50px;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.metallic.gold};
  cursor: pointer;
  padding: 0.5rem;
  margin-right: 0.5rem;
  transition: all ${({ theme }) => theme.transitions.fast};
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    transform: scale(1.05);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: flex;
  }
`;

const MenuText = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.metallic.gold};
`;

const MobileNavDrawer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 80%;
  max-width: 300px;
  height: 100vh;
  background: ${({ theme }) => theme.colors.background.dark};
  transform: ${({ $isOpen }) => $isOpen ? 'translateX(0)' : 'translateX(-100%)'};
  transition: transform ${({ theme }) => theme.transitions.medium};
  z-index: 1002;
  overflow-y: auto;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: none;
  }
`;

const MobileNavContent = styled.div`
  padding: 5rem 1rem 2rem;
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1001;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: none;
  }
`;

const NavContainer = styled.nav`
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    position: fixed;
    top: 0;
    right: 0;
    width: 70%;
    height: 100vh;
    background-color: ${({ theme }) => theme.colors.background.dark};
    padding-top: 5rem;
    transform: ${({ $isOpen }) => ($isOpen ? 'translateX(0)' : 'translateX(100%)')};
    transition: transform ${({ theme }) => theme.transitions.medium};
    z-index: 1000;
    box-shadow: ${({ $isOpen, theme }) => ($isOpen ? theme.shadows.lg : 'none')};
  }
`;

const NavLinks = styled.ul`
  display: flex;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    align-items: center;
  }
`;

const NavItem = styled.li`
  margin: 0 1rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    margin: 1rem 0;
  }
`;

const NavLink = styled(Link)`
  font-size: 1rem;
  font-weight: 600;
  color: #C41E3A;
  padding: 0.5rem;
  position: relative;
  text-decoration: none;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background-color: #C41E3A;
    transform: scaleX(${({ $isActive }) => ($isActive ? 1 : 0)});
    transform-origin: bottom right;
    transition: transform ${({ theme }) => theme.transitions.medium};
  }
  
  &:hover:after {
    transform: scaleX(1);
    transform-origin: bottom left;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
`;

const SearchButton = styled.button`
  background: none;
  border: none;
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.metallic.gold};
  margin-right: 1rem;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  padding: 0.5rem;
  border-radius: 50%;
  
  &:hover {
    background: rgba(212, 175, 55, 0.1);
    transform: scale(1.1);
  }
`;

const SearchBarContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${({ theme }) => theme.colors.background.card};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: 1rem 0;
  transform: ${({ $show }) => $show ? 'translateY(0)' : 'translateY(-100%)'};
  opacity: ${({ $show }) => $show ? 1 : 0};
  visibility: ${({ $show }) => $show ? 'visible' : 'hidden'};
  transition: all ${({ theme }) => theme.transitions.medium};
  z-index: 999;
  border-bottom: 2px solid ${({ theme }) => theme.colors.metallic.gold};
`;

const SearchForm = styled.form`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 1.5rem;
  display: flex;
  gap: 0.5rem;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 1rem 1.5rem;
  font-size: 1.1rem;
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-radius: 50px;
  outline: none;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:focus {
    border-color: ${({ theme }) => theme.colors.metallic.gold};
    box-shadow: ${({ theme }) => theme.shadows.gold};
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.text.muted};
  }
`;

const SearchSubmitButton = styled.button`
  padding: 1rem 2rem;
  background: ${({ theme }) => theme.gradients.gold};
  color: ${({ theme }) => theme.colors.primary};
  border: none;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.gold};
  }
`;

const CartCount = styled.span`
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: #C41E3A;
  color: white;
  font-size: 0.7rem;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

// New styled components for auth
const AuthButtons = styled.div`
  display: flex;
  align-items: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    display: none;
  }
`;

const LoginButton = styled(Link)`
  color: #C41E3A;
  font-weight: 600;
  margin-right: 1rem;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const SignupButton = styled(Link)`
  background-color: #C41E3A;
  color: white;
  font-weight: 600;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  text-decoration: none;
  transition: background-color ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background-color: #a01a30;
  }
`;

const UserMenuContainer = styled.div`
  position: relative;
`;

const UserButton = styled.button`
  background: none;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #C41E3A;
  font-size: 1.2rem;
  cursor: pointer;
  background-color: rgba(196, 30, 58, 0.1);
  
  &:hover {
    background-color: rgba(196, 30, 58, 0.2);
  }
`;

const UserAvatar = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
`;

const UserDropdown = styled.div`
  position: absolute;
  top: 50px;
  right: 0;
  width: 220px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 0.5rem 0;
  z-index: 1001;
`;

const UserInfo = styled.div`
  padding: 1rem;
  border-bottom: 1px solid #eee;
  
  strong {
    display: block;
    margin-bottom: 0.25rem;
  }
  
  small {
    color: #666;
    font-size: 0.8rem;
  }
`;

const DropdownItem = styled(Link)`
  display: block;
  padding: 0.75rem 1rem;
  color: #333;
  text-decoration: none;
  transition: background-color ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background-color: #f5f5f5;
  }
`;

const DropdownButton = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  padding: 0.75rem 1rem;
  color: #C41E3A;
  font-weight: 600;
  cursor: pointer;
  transition: background-color ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background-color: #f5f5f5;
  }
`;

const DropdownDivider = styled.div`
  height: 1px;
  background-color: #eee;
  margin: 0.5rem 0;
`;

export default Header;
